import React, { useEffect, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { Scrollbars } from 'rc-scrollbars';

import { Clock } from '../Icons/Clock';

import {
  TimePicker as TimePickerStyled,
  Button,
  Menu,
  List,
  ListItem,
  Label,
  Info,
  ErrrorMessage,
} from './TimePickerStyled';

export type Time = {
  h: string;
  m: string;
  period: string;
};

type Props = {
  value?: Time;
  onChange: (time: Time) => void;
  label?: string;
  required?: boolean;
  info?: string;
  hasError?: boolean;
  errorMessage?: string;
  testid?: string;
};

const minutes: string[] = Array(60)
  .fill(0)
  .map((x, y) => {
    const sum = x + y;

    return sum < 10 ? `0${sum}` : `${sum}`;
  });

const hors: string[] = Array(12)
  .fill(1)
  .map((x, y) => {
    const sum = x + y;

    return sum < 10 ? `0${sum}` : `${sum}`;
  });

const periods = ['AM', 'PM'];

export const TimePicker: React.FC<Props> = ({
  value,
  onChange,
  label,
  required,
  info,
  hasError,
  errorMessage,
  testid,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [time, setTime] = useState<Time>({ h: '', m: '', period: '' });

  const clickHandler = (value: Time) => {
    const newTime = {
      h: value.h || '01',
      m: value.m || '00',
      period: value.period || 'AM',
    };

    setTime(newTime);
    onChange(newTime);
  };

  useEffect(() => value && setTime(value), [value]);

  let buttonProps: any = {};
  let infoProps: any = {};
  let errorProps: any = {};
  if (testid) {
    buttonProps['data-testid'] = `${testid}-button`;
    infoProps['data-testid'] = `${testid}-time-zone`;
    errorProps['data-testid'] = `${testid}-error-message`;
  }

  return (
    <TimePickerStyled>
      {label && (
        <Label>
          {required && <span>*</span>}
          {label}
        </Label>
      )}
      <OutsideClickHandler onOutsideClick={() => isOpen && setIsOpen(false)}>
        <Button
          onClick={() => setIsOpen(!isOpen)}
          hasError={hasError}
          {...buttonProps}
        >
          <Clock />
          {time.h}
          {time.h ? ':' : ''}
          {time.m} {time.period}
        </Button>
        <Menu isOpen={isOpen}>
          <List>
            <Scrollbars>
              {hors.map((item, i: number) => (
                <ListItem
                  key={i}
                  isActive={item === time.h}
                  onClick={() => clickHandler({ ...time, h: item })}
                  data-testid={`${testid + '-' ?? ''}timepicker-hour-${item}`}
                >
                  {item}
                </ListItem>
              ))}
            </Scrollbars>
          </List>
          <List>
            <Scrollbars>
              {minutes.map((item, i: number) => (
                <ListItem
                  key={i}
                  isActive={item.toString() === time.m}
                  onClick={() => clickHandler({ ...time, m: item })}
                  data-testid={`${testid + '-' ?? ''}timepicker-min-${item}`}
                >
                  {item}
                </ListItem>
              ))}
            </Scrollbars>
          </List>
          <List>
            {periods.map((item, i: number) => (
              <ListItem
                key={i}
                isActive={item === time.period}
                onClick={() => clickHandler({ ...time, period: item })}
                data-testid={`${testid + '-' ?? ''}timepicker-period-${item}`}
              >
                {item}
              </ListItem>
            ))}
          </List>
        </Menu>
        {info && <Info {...infoProps}>{info}</Info>}
        {errorMessage && (
          <ErrrorMessage {...errorProps}>{errorMessage}</ErrrorMessage>
        )}
      </OutsideClickHandler>
    </TimePickerStyled>
  );
};
